import React, { useEffect, useMemo, useState } from 'react';
import { Form, Collapse, Badge, Button } from 'react-bootstrap';
import Select from 'react-select';
import { useAuth0 } from '@auth0/auth0-react';
import { config } from '../../config';

type TagType = {
  tag_type: string;
  values?: (string | number | boolean | null)[];
};

type Assessment = {
  id: string;
  name: string;
  values?: (string | number | boolean | null)[];
};

export type AdvancedFilters = {
  tags: Record<string, (string | number | boolean | null)[]>;
  assessments: Record<string, (string | number | boolean | null)[]>;
};

interface ChannelAdvancedFiltersProps {
  value: AdvancedFilters;
  onChange: (val: AdvancedFilters) => void;
  ready?: boolean;
  onApply?: () => void;
  showChannelFilters?: boolean;
  showTagFilters?: boolean;
  showAssessmentFilters?: boolean;
  channels?: Array<{ value: string; label: string }>;
  channelOptions?: Array<{ value: string; label: string }>;
  onChannelsChange?: (opts: Array<{ value: string; label: string }>) => void;
  selectedAccount?: number | null;
}

const CACHE_DURATION = 4 * 60 * 60 * 1000; // 4 hours
const TAGS_CACHE_KEY = 'ept_response_tags_cache_full';
const ASSESSMENTS_CACHE_KEY = 'ept_response_assessments_cache_full';

const ChannelAdvancedFilters: React.FC<ChannelAdvancedFiltersProps> = ({
  value,
  onChange,
  ready = true,
  onApply,
  showChannelFilters = false,
  showTagFilters = true,
  showAssessmentFilters = true,
  channels = [],
  channelOptions = [],
  onChannelsChange,
  selectedAccount,
}) => {
  const { getIdTokenClaims } = useAuth0();
  const [open, setOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [tagTypes, setTagTypes] = useState<TagType[]>([]);
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const fetchingRef = React.useRef({ tags: false, assessments: false });
  const [activeTagType, setActiveTagType] = useState<string | null>(null);
  const [activeAssessmentId, setActiveAssessmentId] = useState<string | null>(
    null
  );
  const [selectedTagTypes, setSelectedTagTypes] = useState<string[]>([]);
  const singleSideBySide =
    showChannelFilters && showTagFilters !== showAssessmentFilters;

  const selectedTagCount = useMemo(
    () =>
      Object.values(value.tags || {}).reduce(
        (acc, arr) => acc + (arr?.length || 0),
        0
      ),
    [value.tags]
  );
  const selectedAssessmentCount = useMemo(
    () =>
      Object.values(value.assessments || {}).reduce(
        (acc, arr) => acc + (arr?.length || 0),
        0
      ),
    [value.assessments]
  );

  const getCachedData = (key: string) => {
    try {
      const cached = localStorage.getItem(key);
      if (!cached) return null;
      const { data, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp > CACHE_DURATION) {
        localStorage.removeItem(key);
        return null;
      }
      return data;
    } catch {
      return null;
    }
  };

  const setCachedData = (key: string, data: any) => {
    try {
      localStorage.setItem(
        key,
        JSON.stringify({ data, timestamp: Date.now() })
      );
    } catch {}
  };

  const fetchTagTypes = async () => {
    if (fetchingRef.current.tags) return;
    fetchingRef.current.tags = true;
    try {
      const cached = getCachedData(TAGS_CACHE_KEY);
      if (cached) {
        setTagTypes(cached);
        return;
      }
      const getidToken = await getIdTokenClaims();
      const idToken = getidToken?.__raw;
      const options = {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      } as const;
      const response = await fetch(
        `${config.baseURL}/admin/v1/response/tags?account_id=${selectedAccount}`,
        options
      );
      if (response.ok) {
        const result = await response.json();
        const full = (result?.tag_types || []) as TagType[];
        setTagTypes(full);
        setCachedData(TAGS_CACHE_KEY, full);
      }
    } catch (e) {
      console.error('AdvancedFilters: tags fetch error', e);
    } finally {
      fetchingRef.current.tags = false;
    }
  };

  const fetchAssessments = async () => {
    if (fetchingRef.current.assessments) return;
    fetchingRef.current.assessments = true;
    try {
      const cached = getCachedData(ASSESSMENTS_CACHE_KEY);
      if (cached) {
        setAssessments(cached);
        return;
      }
      const getidToken = await getIdTokenClaims();
      const idToken = getidToken?.__raw;
      const options = {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      } as const;
      const response = await fetch(
        `${config.baseURL}/admin/v1/response/assessments`,
        options
      );
      if (response.ok) {
        const result = await response.json();
        const items = (result?.assessments || []) as Assessment[];
        setAssessments(items);
        setCachedData(ASSESSMENTS_CACHE_KEY, items);
      }
    } catch (e) {
      console.error('AdvancedFilters: assessments fetch error', e);
    } finally {
      fetchingRef.current.assessments = false;
    }
  };

  useEffect(() => {
    if (!open || !ready) return;
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        await Promise.all([fetchTagTypes(), fetchAssessments()]);
        if (mounted) {
          if (
            !activeTagType &&
            (Object.keys(value.tags || {})[0] || tagTypes[0]?.tag_type)
          ) {
            setActiveTagType(
              Object.keys(value.tags || {})[0] || tagTypes[0]?.tag_type || null
            );
          }
          if (
            !activeAssessmentId &&
            (Object.keys(value.assessments || {})[0] || assessments[0]?.id)
          ) {
            setActiveAssessmentId(
              Object.keys(value.assessments || {})[0] ||
                assessments[0]?.id ||
                null
            );
          }
          if (selectedTagTypes.length === 0) {
            const existing = Object.keys(value.tags || {});
            if (existing.length > 0) {
              setSelectedTagTypes(existing);
            } else if (tagTypes[0]?.tag_type) {
              setSelectedTagTypes([tagTypes[0].tag_type]);
            }
          }
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ready]);

  const handleTagTypeChange = (
    tagType: string,
    values: (string | number | boolean | null)[]
  ) => {
    const next: AdvancedFilters = {
      ...value,
      tags: { ...(value.tags || {}), [tagType]: values },
    };
    if (!values || values.length === 0) delete next.tags[tagType];
    onChange(next);
  };

  const handleAssessmentChange = (
    assessmentId: string,
    values: (string | number | boolean | null)[]
  ) => {
    const next: AdvancedFilters = {
      ...value,
      assessments: { ...(value.assessments || {}), [assessmentId]: values },
    };
    if (!values || values.length === 0) delete next.assessments[assessmentId];
    onChange(next);
  };

  const clearAll = () => {
    onChange({ tags: {}, assessments: {} });
    if (showChannelFilters && onChannelsChange) {
      onChannelsChange([] as any);
    }
  };

  return (
    <div style={{ marginTop: '16px' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '8px',
        }}
      >
        <button
          onClick={() => setOpen(!open)}
          className={`btn btn-sm ${open ? 'btn-primary' : 'btn-outline-primary'}`}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
        >
          <span>⚙️</span>
          <span>Advanced Filters</span>
        </button>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {showChannelFilters && (channels?.length || 0) > 0 && (
            <Badge bg="secondary" title="Selected Channels">
              {channels?.length || 0}
            </Badge>
          )}
          {selectedTagCount > 0 && (
            <Badge bg="secondary" title="Selected Tag Filters">
              {selectedTagCount}
            </Badge>
          )}
          {selectedAssessmentCount > 0 && (
            <Badge bg="secondary" title="Selected Assessment Filters">
              {selectedAssessmentCount}
            </Badge>
          )}
          {((channels?.length || 0) > 0 ||
            selectedTagCount > 0 ||
            selectedAssessmentCount > 0) && (
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={clearAll}
              disabled={loading}
            >
              Clear
            </button>
          )}
        </div>
      </div>

      <Collapse in={open}>
        <div
          style={{
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            padding: 16,
          }}
        >
          {showChannelFilters && !singleSideBySide && (
            <div style={{ marginBottom: 16 }}>
              <h6 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
                Filter by Channels
              </h6>
              <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>
                Select channels to apply to this view
              </p>
              <Form.Group style={{ marginBottom: 12 }}>
                <Form.Label
                  style={{ fontSize: 12, fontWeight: 500, marginBottom: 6 }}
                >
                  Channels
                </Form.Label>
                <Select
                  isMulti
                  options={channelOptions}
                  value={channels}
                  onChange={(opts: any) =>
                    onChannelsChange && onChannelsChange(opts || [])
                  }
                  isDisabled={loading}
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                  placeholder="All Channels"
                />
              </Form.Group>
            </div>
          )}

          {singleSideBySide && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 16,
                marginBottom: 16,
              }}
            >
              <div>
                <h6 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
                  Filter by Channels
                </h6>
                <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>
                  Select channels to apply to this view
                </p>
                <Form.Group style={{ marginBottom: 12 }}>
                  <Form.Label
                    style={{ fontSize: 12, fontWeight: 500, marginBottom: 6 }}
                  >
                    Channels
                  </Form.Label>
                  <Select
                    isMulti
                    options={channelOptions}
                    value={channels}
                    onChange={(opts: any) =>
                      onChannelsChange && onChannelsChange(opts || [])
                    }
                    isDisabled={loading}
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                    placeholder="All Channels"
                  />
                </Form.Group>
              </div>
              <div>
                {showTagFilters && !showAssessmentFilters && (
                  <>
                    <h6
                      style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}
                    >
                      Filter by Tag Type
                    </h6>
                    <p
                      style={{
                        fontSize: 12,
                        color: '#6b7280',
                        marginBottom: 12,
                      }}
                    >
                      Pick one or more tag types, then choose values for the
                      active tag type
                    </p>
                    <Form.Group style={{ marginBottom: 8 }}>
                      <Form.Label
                        style={{
                          fontSize: 12,
                          fontWeight: 500,
                          marginBottom: 6,
                        }}
                      >
                        Tag Type
                      </Form.Label>
                      <Select
                        isSearchable
                        isMulti
                        options={tagTypes.map(tt => ({
                          value: tt.tag_type,
                          label: tt.tag_type,
                        }))}
                        value={selectedTagTypes.map(tt => ({
                          value: tt,
                          label: tt,
                        }))}
                        onChange={(opts: any) => {
                          const arr = (opts || []).map((o: any) =>
                            String(o.value)
                          );
                          setSelectedTagTypes(arr);
                          setActiveTagType(
                            arr.length ? arr[arr.length - 1] : null
                          );
                        }}
                        isDisabled={loading}
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: base => ({ ...base, zIndex: 9999 }),
                        }}
                        placeholder="Select tag type(s)"
                      />
                    </Form.Group>
                    {activeTagType && (
                      <Form.Group style={{ marginBottom: 12 }}>
                        <Form.Label
                          style={{
                            fontSize: 12,
                            fontWeight: 500,
                            marginBottom: 6,
                          }}
                        >
                          Value
                        </Form.Label>
                        <Select
                          options={(
                            tagTypes.find(t => t.tag_type === activeTagType)
                              ?.values || []
                          ).map(v => ({ value: String(v), label: String(v) }))}
                          value={
                            (value.tags?.[activeTagType] || []).length
                              ? {
                                  value: String(
                                    (value.tags?.[activeTagType] || [])[0]
                                  ),
                                  label: String(
                                    (value.tags?.[activeTagType] || [])[0]
                                  ),
                                }
                              : null
                          }
                          onChange={(opt: any) =>
                            handleTagTypeChange(
                              activeTagType,
                              opt ? [opt.value] : []
                            )
                          }
                          isDisabled={loading}
                          menuPortalTarget={document.body}
                          styles={{
                            menuPortal: base => ({ ...base, zIndex: 9999 }),
                          }}
                          placeholder="Select value"
                          isClearable
                        />
                      </Form.Group>
                    )}
                  </>
                )}

                {showAssessmentFilters && !showTagFilters && (
                  <>
                    <h6
                      style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}
                    >
                      Filter by Assessment
                    </h6>
                    <p
                      style={{
                        fontSize: 12,
                        color: '#6b7280',
                        marginBottom: 12,
                      }}
                    >
                      Pick an assessment, then choose evaluation values
                    </p>
                    <Form.Group style={{ marginBottom: 8 }}>
                      <Form.Label
                        style={{
                          fontSize: 12,
                          fontWeight: 500,
                          marginBottom: 6,
                        }}
                      >
                        Assessment
                      </Form.Label>
                      <Select
                        isSearchable
                        options={assessments.map(a => ({
                          value: a.id,
                          label: a.name,
                        }))}
                        value={
                          activeAssessmentId
                            ? {
                                value: activeAssessmentId,
                                label:
                                  assessments.find(
                                    a => a.id === activeAssessmentId
                                  )?.name || activeAssessmentId,
                              }
                            : null
                        }
                        onChange={(opt: any) =>
                          setActiveAssessmentId(opt?.value || null)
                        }
                        isDisabled={loading}
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: base => ({ ...base, zIndex: 9999 }),
                        }}
                        placeholder="Select assessment"
                      />
                    </Form.Group>
                    {activeAssessmentId && (
                      <Form.Group style={{ marginBottom: 12 }}>
                        <Form.Label
                          style={{
                            fontSize: 12,
                            fontWeight: 500,
                            marginBottom: 6,
                          }}
                        >
                          Value
                        </Form.Label>
                        <Select
                          options={(
                            assessments.find(a => a.id === activeAssessmentId)
                              ?.values || []
                          ).map(v => ({ value: String(v), label: String(v) }))}
                          value={
                            (value.assessments?.[activeAssessmentId] || [])
                              .length
                              ? {
                                  value: String(
                                    (value.assessments?.[activeAssessmentId] ||
                                      [])[0]
                                  ),
                                  label: String(
                                    (value.assessments?.[activeAssessmentId] ||
                                      [])[0]
                                  ),
                                }
                              : null
                          }
                          onChange={(opt: any) =>
                            handleAssessmentChange(
                              activeAssessmentId,
                              opt ? [opt.value] : []
                            )
                          }
                          isDisabled={loading}
                          menuPortalTarget={document.body}
                          styles={{
                            menuPortal: base => ({ ...base, zIndex: 9999 }),
                          }}
                          placeholder="Select value"
                          isClearable
                        />
                      </Form.Group>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {!singleSideBySide && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 16,
              }}
            >
              {showTagFilters && (
                <div>
                  <h6
                    style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}
                  >
                    Filter by Tag Type
                  </h6>
                  <p
                    style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}
                  >
                    Pick one or more tag types, then choose values for the
                    active tag type
                  </p>

                  <Form.Group style={{ marginBottom: 8 }}>
                    <Form.Label
                      style={{ fontSize: 12, fontWeight: 500, marginBottom: 6 }}
                    >
                      Tag Type
                    </Form.Label>
                    <Select
                      isSearchable
                      isMulti
                      options={tagTypes.map(tt => ({
                        value: tt.tag_type,
                        label: tt.tag_type,
                      }))}
                      value={selectedTagTypes.map(tt => ({
                        value: tt,
                        label: tt,
                      }))}
                      onChange={(opts: any) => {
                        const arr = (opts || []).map((o: any) =>
                          String(o.value)
                        );
                        setSelectedTagTypes(arr);
                        setActiveTagType(
                          arr.length ? arr[arr.length - 1] : null
                        );
                      }}
                      isDisabled={loading}
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: base => ({ ...base, zIndex: 9999 }),
                      }}
                      placeholder="Select tag type(s)"
                    />
                  </Form.Group>

                  {activeTagType && (
                    <Form.Group style={{ marginBottom: 12 }}>
                      <Form.Label
                        style={{
                          fontSize: 12,
                          fontWeight: 500,
                          marginBottom: 6,
                        }}
                      >
                        Value
                      </Form.Label>
                      <Select
                        options={(
                          tagTypes.find(t => t.tag_type === activeTagType)
                            ?.values || []
                        ).map(v => ({
                          value: String(v),
                          label: String(v),
                        }))}
                        value={
                          (value.tags?.[activeTagType] || []).length
                            ? {
                                value: String(
                                  (value.tags?.[activeTagType] || [])[0]
                                ),
                                label: String(
                                  (value.tags?.[activeTagType] || [])[0]
                                ),
                              }
                            : null
                        }
                        onChange={(opt: any) =>
                          handleTagTypeChange(
                            activeTagType,
                            opt ? [opt.value] : []
                          )
                        }
                        isDisabled={loading}
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: base => ({ ...base, zIndex: 9999 }),
                        }}
                        placeholder="Select value"
                        isClearable
                      />
                    </Form.Group>
                  )}

                  {selectedTagTypes.length > 0 && (
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 6,
                        marginTop: 6,
                      }}
                    >
                      {selectedTagTypes.map(tt => (
                        <button
                          key={tt}
                          className={`btn btn-xs ${activeTagType === tt ? 'btn-primary' : 'btn-outline-secondary'}`}
                          style={{
                            padding: '2px 8px',
                            fontSize: 11,
                            borderRadius: 12,
                          }}
                          onClick={() => setActiveTagType(tt)}
                        >
                          {tt}
                          {Array.isArray(value.tags?.[tt]) && (
                            <span
                              className="badge bg-light text-dark ms-1"
                              style={{ fontSize: 10 }}
                            >
                              {value.tags?.[tt]?.length || 0}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {showAssessmentFilters && (
                <div>
                  <h6
                    style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}
                  >
                    Filter by Assessment
                  </h6>
                  <p
                    style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}
                  >
                    Pick an assessment, then choose evaluation values
                  </p>

                  <Form.Group style={{ marginBottom: 8 }}>
                    <Form.Label
                      style={{ fontSize: 12, fontWeight: 500, marginBottom: 6 }}
                    >
                      Assessment
                    </Form.Label>
                    <Select
                      isSearchable
                      options={assessments.map(a => ({
                        value: a.id,
                        label: a.name,
                      }))}
                      value={
                        activeAssessmentId
                          ? {
                              value: activeAssessmentId,
                              label:
                                assessments.find(
                                  a => a.id === activeAssessmentId
                                )?.name || activeAssessmentId,
                            }
                          : null
                      }
                      onChange={(opt: any) =>
                        setActiveAssessmentId(opt?.value || null)
                      }
                      isDisabled={loading}
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: base => ({ ...base, zIndex: 9999 }),
                      }}
                      placeholder="Select assessment"
                    />
                  </Form.Group>

                  {activeAssessmentId && (
                    <Form.Group style={{ marginBottom: 12 }}>
                      <Form.Label
                        style={{
                          fontSize: 12,
                          fontWeight: 500,
                          marginBottom: 6,
                        }}
                      >
                        Value
                      </Form.Label>
                      <Select
                        options={(
                          assessments.find(a => a.id === activeAssessmentId)
                            ?.values || []
                        ).map(v => ({
                          value: String(v),
                          label: String(v),
                        }))}
                        value={
                          (value.assessments?.[activeAssessmentId] || []).length
                            ? {
                                value: String(
                                  (value.assessments?.[activeAssessmentId] ||
                                    [])[0]
                                ),
                                label: String(
                                  (value.assessments?.[activeAssessmentId] ||
                                    [])[0]
                                ),
                              }
                            : null
                        }
                        onChange={(opt: any) =>
                          handleAssessmentChange(
                            activeAssessmentId,
                            opt ? [opt.value] : []
                          )
                        }
                        isDisabled={loading}
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: base => ({ ...base, zIndex: 9999 }),
                        }}
                        placeholder="Select value"
                        isClearable
                      />
                    </Form.Group>
                  )}

                  {Object.keys(value.assessments || {}).length > 0 && (
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 6,
                        marginTop: 6,
                      }}
                    >
                      {Object.keys(value.assessments || {}).map(id => (
                        <button
                          key={id}
                          className={`btn btn-xs ${activeAssessmentId === id ? 'btn-primary' : 'btn-outline-secondary'}`}
                          style={{
                            padding: '2px 8px',
                            fontSize: 11,
                            borderRadius: 12,
                          }}
                          onClick={() => setActiveAssessmentId(id)}
                          title={assessments.find(a => a.id === id)?.name || id}
                        >
                          {assessments.find(a => a.id === id)?.name || id}
                          <span
                            className="badge bg-light text-dark ms-1"
                            style={{ fontSize: 10 }}
                          >
                            {value.assessments?.[id]?.length || 0}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 8 }}>
            Payload (preview):
            <code style={{ display: 'block', whiteSpace: 'pre-wrap' }}>
              {JSON.stringify(
                {
                  ...(showChannelFilters
                    ? { channels: (channels || []).map(c => c.value) }
                    : {}),
                  ...(showTagFilters ? { tags: value.tags || {} } : {}),
                  ...(showAssessmentFilters
                    ? { assessments: value.assessments || {} }
                    : {}),
                },
                null,
                2
              )}
            </code>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: 12,
            }}
          >
            <Button
              variant="primary"
              size="sm"
              onClick={() => onApply && onApply()}
              disabled={loading}
            >
              Apply
            </Button>
          </div>
        </div>
      </Collapse>
    </div>
  );
};

export default ChannelAdvancedFilters;

